import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../config/axios';
import { MdAdd, MdEdit, MdDelete, MdClose, MdArrowBack, MdChevronRight, MdFolder, MdOpenInNew, MdPending, MdCheckCircle, MdAttachFile, MdNote, MdUpload, MdDownload } from 'react-icons/md';
import { CiMenuKebab } from "react-icons/ci";
import { useLanguage } from '../../context/LanguageContext';

const ProjectDetail = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectFiles, setProjectFiles] = useState([]);
  const [projectNotes, setProjectNotes] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showAddFileForm, setShowAddFileForm] = useState(false);
  const [showAddNoteForm, setShowAddNoteForm] = useState(false);
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [newFile, setNewFile] = useState({
    project_file_name: '',
    project_file_description: ''
  });
  const [newNote, setNewNote] = useState({
    project_notes: ''
  });
  const [newMember, setNewMember] = useState({
    project_member_name: '',
    assigned_hours: '',
    project_role: '',
    custom_member_name: '',
    custom_role: ''
  });
  const [editingNote, setEditingNote] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [fileDescription, setFileDescription] = useState('');
  const fileInputRef = useRef(null);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: null,
    showCancel: true
  });
  const [showMeetingSummary, setShowMeetingSummary] = useState(false);
  const [meetingSummary, setMeetingSummary] = useState('');
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  useEffect(() => {
    fetchProject();
    fetchProjectFiles();
    fetchProjectNotes();
    fetchProjectMembers();
    fetchAgents();
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${id}`);
      if (response.data.success) {
        setProject(response.data.project);
      }
    } catch (error) {
      console.error('Proje yüklenirken hata:', error);
      showConfirmPopup({
        title: t('common.error'),
        type: 'error',
        message: 'Proje yüklenirken hata oluştu',
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectFiles = async () => {
    try {
      const response = await api.get(`/projects/${id}/files`);
      if (response.data.success) {
        setProjectFiles(response.data.files);
      }
    } catch (error) {
      console.error('Proje dosyaları yüklenirken hata:', error);
    }
  };

  const fetchProjectNotes = async () => {
    try {
      const response = await api.get(`/projects/${id}/notes`);
      if (response.data.success) {
        setProjectNotes(response.data.notes);
      }
    } catch (error) {
      console.error('Proje notları yüklenirken hata:', error);
    }
  };

  const fetchProjectMembers = async () => {
    try {
      const response = await api.get(`/projects/${id}/members`);
      setProjectMembers(response.data);
    } catch (error) {
      console.error('Proje üyeleri yüklenirken hata:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get('/agents');
      setAgents(response.data);
    } catch (error) {
      console.error('Agentler yüklenirken hata:', error);
    }
  };

  const showConfirmPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || 'Onay',
      message: options.message,
      type: options.type || 'confirm',
      onConfirm: options.onConfirm,
      showCancel: options.showCancel ?? true
    });
  };

  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  const handleFileUpload = async (uploadedFiles) => {
    if (!uploadedFiles || uploadedFiles.length === 0) return;
    
    setIsUploading(true);
    const formData = new FormData();
    
    // Append each file to the form data
    for (let i = 0; i < uploadedFiles.length; i++) {
      formData.append('files', uploadedFiles[i]);
    }
    
    // Add project ID and description
    formData.append('project_id', id);
    formData.append('file_description', fileDescription || 'No file description');
    
    // Get user info from localStorage
    const userInfo = JSON.parse(localStorage.getItem('user'));
    formData.append('uploaded_by_name', userInfo?.fullName || userInfo?.username || 'Unknown');
    
    try {
      const response = await api.post('/upload-project-files', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        showConfirmPopup({
          title: t('common.success'),
          message: t('projectDetail.fileUploaded'),
          type: 'success',
          showCancel: false
        });
        
        // Reset file description
        setFileDescription('');
        
        // Refresh the file list
        fetchProjectFiles();
      } else {
        throw new Error(response.data.message || 'File upload failed');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      showConfirmPopup({
        title: t('common.error'),
        message: t('projectDetail.fileUploadError') + ': ' + (error.response?.data?.message || error.message),
        type: 'error',
        showCancel: false
      });
    } finally {
      setIsUploading(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };
  
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files);
    }
  };
  
  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAddFile = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post(`/projects/${id}/files`, newFile);
      if (response.data.success) {
        setNewFile({ project_file_name: '', project_file_description: '' });
        setShowAddFileForm(false);
        fetchProjectFiles();
        showConfirmPopup({
          title: t('common.success'),
          message: 'Dosya başarıyla eklendi',
          type: 'success',
          showCancel: false
        });
      }
    } catch (error) {
      showConfirmPopup({
        title: t('common.error'),
        message: 'Dosya eklenirken hata oluştu',
        type: 'error',
        showCancel: false
      });
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      // Get user info from localStorage
      const userInfo = JSON.parse(localStorage.getItem('user'));
      const createdBy = userInfo?.fullName || userInfo?.username || 'Unknown';
      
      const response = await api.post(`/projects/${id}/notes`, {
        ...newNote,
        created_by: createdBy
      });
      if (response.data.success) {
        setNewNote({ project_notes: '' });
        setShowAddNoteForm(false);
        fetchProjectNotes();
        showConfirmPopup({
          title: t('common.success'),
          message: t('projectDetail.noteAdded'),
          type: 'success',
          showCancel: false
        });
      }
    } catch (error) {
      showConfirmPopup({
        title: t('common.error'),
        message: t('projectDetail.noteAddError'),
        type: 'error',
        showCancel: false
      });
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNewNote({ project_notes: note.project_notes });
    setShowAddNoteForm(true);
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/projects/${id}/notes/${editingNote.id}`, {
        project_notes: newNote.project_notes
      });
      if (response.data.success) {
        setNewNote({ project_notes: '' });
        setEditingNote(null);
        setShowAddNoteForm(false);
        fetchProjectNotes();
        showConfirmPopup({
          title: t('common.success'),
          message: t('projectDetail.noteUpdated'),
          type: 'success',
          showCancel: false
        });
      }
    } catch (error) {
      showConfirmPopup({
        title: t('common.error'),
        message: t('projectDetail.noteUpdateError'),
        type: 'error',
        showCancel: false
      });
    }
  };

  // Project Members CRUD functions
  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      const memberData = {
        project_member_name: newMember.project_member_name === 'Other' ? newMember.custom_member_name : newMember.project_member_name,
        project_role: newMember.project_role === 'Other' ? newMember.custom_role : newMember.project_role,
        assigned_hours: newMember.assigned_hours
      };
      
      const response = await api.post(`/projects/${id}/members`, memberData);
      if (response.data.success) {
        setNewMember({ project_member_name: '', assigned_hours: '', project_role: '', custom_member_name: '', custom_role: '' });
        setShowAddMemberForm(false);
        fetchProjectMembers();
        showConfirmPopup({
          title: t('common.success'),
          message: t('projectDetail.memberAddedSuccess'),
          type: 'success',
          showCancel: false
        });
      }
    } catch (error) {
      showConfirmPopup({
        title: t('common.error'),
        message: t('projectDetail.memberAddError'),
        type: 'error',
        showCancel: false
      });
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setNewMember({
      project_member_name: member.project_member_name,
      assigned_hours: member.assigned_hours,
      project_role: member.project_role,
      custom_member_name: '',
      custom_role: ''
    });
    setShowAddMemberForm(true);
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      const memberData = {
        project_member_name: newMember.project_member_name === 'Other' ? newMember.custom_member_name : newMember.project_member_name,
        project_role: newMember.project_role === 'Other' ? newMember.custom_role : newMember.project_role,
        assigned_hours: newMember.assigned_hours
      };
      
      const response = await api.put(`/projects/${id}/members/${editingMember.id}`, memberData);
      if (response.data.success) {
        setNewMember({ project_member_name: '', assigned_hours: '', project_role: '', custom_member_name: '', custom_role: '' });
        setEditingMember(null);
        setShowAddMemberForm(false);
        fetchProjectMembers();
        showConfirmPopup({
          title: t('common.success'),
          message: t('projectDetail.memberUpdatedSuccess'),
          type: 'success',
          showCancel: false
        });
      }
    } catch (error) {
      showConfirmPopup({
        title: t('common.error'),
        message: t('projectDetail.memberUpdateError'),
        type: 'error',
        showCancel: false
      });
    }
  };

  const handleDeleteMember = (memberId) => {
    showConfirmPopup({
      title: t('projectDetail.deleteMember'),
      message: t('projectDetail.confirmDeleteMember'),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/projects/${id}/members/${memberId}`);
          if (response.data.success) {
            fetchProjectMembers();
            showConfirmPopup({
              title: t('common.success'),
              message: t('projectDetail.memberDeletedSuccess'),
              type: 'success',
              showCancel: false
            });
          }
        } catch (error) {
          showConfirmPopup({
            title: t('common.error'),
            message: t('projectDetail.memberDeleteError'),
            type: 'error',
            showCancel: false
          });
        }
      }
    });
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await api.get(`/download-project-file/${fileId}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.remove();
      }, 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      showConfirmPopup({
        title: t('common.error'),
        message: t('projectDetail.fileDownloadError'),
        type: 'error',
        showCancel: false
      });
    }
  };

  const handleDeleteFile = async (fileId) => {
    showConfirmPopup({
      title: 'Dosyayı Sil',
      message: 'Bu dosyayı silmek istediğinizden emin misiniz?',
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/projects/${id}/files/${fileId}`);
          if (response.data.success) {
            fetchProjectFiles();
            showConfirmPopup({
              title: t('common.success'),
              message: 'Dosya başarıyla silindi',
              type: 'success',
              showCancel: false
            });
          }
        } catch (error) {
          showConfirmPopup({
            title: t('common.error'),
            message: t('projectDetail.fileDeleteError'),
            type: 'error',
            showCancel: false
          });
        }
      }
    });
  };

  const handleDeleteNote = async (noteId) => {
    showConfirmPopup({
      title: t('projectDetail.deleteNote'),
      message: t('projectDetail.confirmDeleteNote'),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/projects/${id}/notes/${noteId}`);
          if (response.data.success) {
            fetchProjectNotes();
            showConfirmPopup({
              title: t('common.success'),
              message: 'Not başarıyla silindi',
              type: 'success',
              showCancel: false
            });
          }
        } catch (error) {
          showConfirmPopup({
            title: t('common.error'),
            message: 'Not silinirken hata oluştu',
            type: 'error',
            showCancel: false
          });
        }
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300';
      case 'inactive':
        return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300';
      case 'planning':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300';
      case 'in_progress':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300';
      case 'completed':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300';
      case 'cancelled':
        return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return t('projectSettings.active');
      case 'inactive':
        return t('projectSettings.inactive');
      case 'planning':
        return t('projectSettings.planning');
      case 'in_progress':
        return t('projectSettings.inProgress');
      case 'completed':
        return t('projectSettings.completed');
      case 'cancelled':
        return t('projectSettings.cancelled');
      default:
        return status;
    }
  };

  // Gemini AI ile toplantı notları özeti oluştur
  const generateMeetingSummary = async () => {
    if (projectNotes.length === 0) {
      setMeetingSummary('Toplantı notu bulunamadı.');
      setShowMeetingSummary(true);
      return;
    }

    setIsGeneratingSummary(true);
    
    try {
      // Toplantı notlarını tarihe göre grupla
      const notesByDate = {};
      projectNotes.forEach(note => {
        const date = new Date(note.created_at).toLocaleDateString('tr-TR');
        if (!notesByDate[date]) {
          notesByDate[date] = [];
        }
        notesByDate[date].push(note);
      });

      // Gemini API'ye gönderilecek prompt oluştur
      let prompt = 'Aşağıdaki toplantı notlarını analiz edip özet çıkar:\n\n';
      
      Object.keys(notesByDate).forEach(date => {
        prompt += `${date} Tarihli Toplantı Notları:\n`;
        notesByDate[date].forEach(note => {
          prompt += `- ${note.project_notes}\n`;
        });
        prompt += '\n';
      });

      prompt += 'Bu notları analiz edip:\n';
      prompt += '1. Her tarih için ayrı ayrı özet çıkar\n';
      prompt += '2. Tüm notların genel özetini çıkar\n';
      prompt += '3. Türkçe olarak yanıtla\n';
      prompt += '4. Format: "01.01.2025 Tarihli Toplantı notu özeti aşağıdaki gibidir ; [özet]" şeklinde her tarih için\n';
      prompt += '5. Son olarak "GENEL Proje Özeti;" başlığı altında genel özet';

      // Gemini API çağrısı
      const response = await fetch('https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=AIzaSyAaHX9SidJ8nUdNLty8lcAXGnhDuRQ3KTQ', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }]
        })
      });

      const data = await response.json();
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        setMeetingSummary(data.candidates[0].content.parts[0].text);
      } else {
        setMeetingSummary('Özet oluşturulurken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Gemini API error:', error);
      setMeetingSummary('Özet oluşturulurken bir hata oluştu.');
    } finally {
      setIsGeneratingSummary(false);
      setShowMeetingSummary(true);
    }
  };

  if (loading) return <div>{t('common.loading')}</div>;
  if (!project) return <div>Proje bulunamadı</div>;

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center text-sm mb-6 pt-4">
        <div className="flex items-center bg-[#232b3b] px-4 py-2 rounded-md shadow-sm">
          <Link 
            to="/projects" 
            className="text-[#fff] hover:text-[gray] font-medium transition-colors duration-200"
          >
            {t('navigation.projects')}
          </Link>
          <MdChevronRight className="mx-2 text-[#fff]" size={20} />
          <span className="text-[#fff] font-medium">
            {project.project_name}
          </span>
        </div>
      </nav>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Project Details Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
            <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('projectDetail.projectDetails')}</h1>
          </div>
          <div className="overflow-hidden">
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('projectDetail.projectName')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{project.project_name}</td>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('projectDetail.status')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">
                      <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${getStatusColor(project.project_status)}`}>
                        {getStatusLabel(project.project_status)}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('projectDetail.startDate')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{project.project_start_date || '-'}</td>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('projectDetail.endDate')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{project.project_end_date || '-'}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('projectDetail.vendor')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{project.project_vendor || '-'}</td>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('projectDetail.vendorPM')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{project.vendor_pm || '-'}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('projectDetail.assignedFrom')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{project.assigned_from || '-'}</td>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('projectDetail.createdBy')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{project.created_by}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('projectDetail.hours')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{project.project_hours || '-'}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('projectDetail.projectDescription')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white" colSpan="3">{project.project_description || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4 p-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.projectName')}</div>
                <div className="text-gray-900 dark:text-white">{project.project_name}</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.status')}</div>
                <div className="text-gray-900 dark:text-white">
                  <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${getStatusColor(project.project_status)}`}>
                    {getStatusLabel(project.project_status)}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.startDate')}</div>
                  <div className="text-gray-900 dark:text-white">{project.project_start_date || '-'}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.endDate')}</div>
                  <div className="text-gray-900 dark:text-white">{project.project_end_date || '-'}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.vendor')}</div>
                  <div className="text-gray-900 dark:text-white">{project.project_vendor || '-'}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.vendorPM')}</div>
                  <div className="text-gray-900 dark:text-white">{project.vendor_pm || '-'}</div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.assignedFrom')}</div>
                <div className="text-gray-900 dark:text-white">{project.assigned_from || '-'}</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.createdBy')}</div>
                <div className="text-gray-900 dark:text-white">{project.created_by}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.hours')}</div>
                  <div className="text-gray-900 dark:text-white">{project.project_hours || '-'}</div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.projectDescription')}</div>
                <div className="text-gray-900 dark:text-white">{project.project_description || '-'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Project Members Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('projectDetail.projectMembers')}</h2>
            <button
              onClick={() => {
                setEditingMember(null);
                setNewMember({ project_member_name: '', assigned_hours: '', project_role: '', custom_member_name: '', custom_role: '' });
                setShowAddMemberForm(true);
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-sm"
            >
{t('projectDetail.addMember')}
            </button>
          </div>
          <div className="overflow-hidden">
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('projectDetail.memberName')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('projectDetail.projectRole')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('projectDetail.assignedHours')}</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('projectDetail.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {projectMembers.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400 rounded-bl-lg rounded-br-lg">
{t('projectDetail.noProjectMembers')}
                      </td>
                    </tr>
                  ) : (
                    projectMembers.map((member, index) => {
                      const isLast = index === projectMembers.length - 1;
                      return (
                        <tr key={member.id}>
                          <td className={`px-4 py-2 text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                            {member.project_member_name}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{member.project_role}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{member.assigned_hours} {t('projectDetail.hours')}</td>
                          <td className={`px-4 py-2 text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-br-lg' : ''}`}>
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEditMember(member)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Edit member"
                              >
                                <MdEdit size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteMember(member.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete member"
                              >
                                <MdDelete size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4 p-4">
                {projectMembers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400 dark:text-gray-500 text-sm">{t('projectDetail.noProjectMembers')}</p>
                  </div>
                ) : (
                  projectMembers.map((member) => (
                    <div key={member.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                      {/* Member Name & Project Role */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.memberName')}</div>
                          <div className="text-sm text-gray-900 dark:text-white font-medium">{member.project_member_name}</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.projectRole')}</div>
                          <div className="text-sm text-gray-900 dark:text-white">{member.project_role}</div>
                        </div>
                      </div>

                      {/* Assigned Hours & Actions */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.assignedHours')}</div>
                          <div className="text-sm text-gray-900 dark:text-white">{member.assigned_hours} {t('projectDetail.hours')}</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('projectDetail.actions')}</div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditMember(member)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Edit member"
                            >
                              <MdEdit size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteMember(member.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete member"
                            >
                              <MdDelete size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
          </div>
        </div>

        {/* Project Files Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
            <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('projectDetail.projectFiles')}</h2>
          </div>
          <div className="p-6">
            <div className="mb-4">
              <label htmlFor="fileDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('projectDetail.fileDescription')}
              </label>
              <input
                type="text"
                id="fileDescription"
                value={fileDescription}
                onChange={(e) => setFileDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('projectDetail.enterFileDescription')}
              />
            </div>
            
            <div 
              className={`border-2 border-dashed rounded-md p-6 ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
            >
              <div className="text-center">
                <MdUpload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('projectDetail.dragDropFilesHere')}
                  </p>
                  <button
                    type="button"
                    className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={openFileSelector}
                    disabled={isUploading}
                  >
                    {isUploading ? t('common.loading') : t('projectDetail.selectFiles')}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileInputChange}
                    disabled={isUploading}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  PDF, Word, Excel, PowerPoint, ZIP, TXT, LOG (max. 10MB)
                </p>
              </div>
            </div>
          </div>

          {/* Files Table */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
              <h3 className="text-sm font-medium text-gray-800 dark:text-white">{t('projectDetail.uploadedFiles')}</h3>
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('projectDetail.fileName')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('projectDetail.description')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('projectDetail.uploader')}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('projectDetail.uploadDate')}
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('projectDetail.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {!projectFiles || projectFiles.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-2 text-center text-gray-500 dark:text-gray-400">
                        {t('projectDetail.noFilesUploaded')}
                      </td>
                    </tr>
                  ) : (
                    projectFiles.map((file, index) => {
                      const isLast = index === projectFiles.length - 1;
                      return (
                        <tr key={file.id}>
                          <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                            {file.project_file_name}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {file.project_file_description || t('projectDetail.noDescription')}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {file.uploaded_by_name || file.uploaded_by || 'Bilinmiyor'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(file.created_at).toLocaleDateString()}
                          </td>
                          <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 ${isLast ? 'rounded-br-lg' : ''}`}>
                            <div className="flex items-center justify-end space-x-3">
                              <button
                                onClick={() => handleDownloadFile(file.id, file.project_file_name)}
                                className="text-blue-600 hover:text-blue-800"
                                title="İndir"
                              >
                                <MdDownload size={20} />
                              </button>
                              <button
                                onClick={() => handleDeleteFile(file.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Sil"
                              >
                                <MdDelete size={20} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden p-4">
              {!projectFiles || projectFiles.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-400 dark:text-gray-500 text-sm">{t('projectDetail.noFilesUploaded')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {projectFiles.map((file, index) => (
                    <div key={file.id} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                      <div className="space-y-2">
                        {/* File Name */}
                        <div className="flex justify-between items-start">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Dosya Adı</span>
                          <span className="text-xs text-gray-700 dark:text-gray-300 font-medium truncate max-w-[200px]">{file.project_file_name}</span>
                        </div>
                        
                        {/* Description */}
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Açıklama</span>
                          <p className="text-xs text-gray-700 dark:text-gray-300 mt-1 line-clamp-2">
                            {file.project_file_description || t('projectDetail.noDescription')}
                          </p>
                        </div>
                        
                        {/* Uploader & Date */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Yükleyen</span>
                            <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                              {file.uploaded_by_name || file.uploaded_by || 'Bilinmiyor'}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Yüklenme Tarihi</span>
                            <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                              {new Date(file.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex justify-end items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <button
                            onClick={() => handleDownloadFile(file.id, file.project_file_name)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="İndir"
                          >
                            <MdDownload size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteFile(file.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Sil"
                          >
                            <MdDelete size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Project Notes Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('projectDetail.projectNotes')}</h2>
            <div className="flex gap-2">
              <button
                onClick={generateMeetingSummary}
                disabled={isGeneratingSummary}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50"
              >
                {isGeneratingSummary ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Özet Oluşturuluyor...
                  </>
                ) : (
                  <>
                    <MdNote size={16} />
                    Toplantı Notları Özeti
                  </>
                )}
              </button>
              <button
                onClick={() => setShowAddNoteForm(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-sm"
              >
                <MdAdd size={16} />
                {t('projectDetail.addNote')}
              </button>
            </div>
          </div>
          <div className="p-6">
            {projectNotes.length === 0 ? (
              <div className="text-center py-8">
                <MdNote size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-400 dark:text-gray-500 text-sm">{t('projectDetail.noNotes')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {projectNotes.map((note) => (
                  <div key={note.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{note.project_notes}</p>
                      <div className="mt-2">
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm text-gray-500 dark:text-gray-500">
                            {t('projectDetail.createdAt')}: {new Date(note.created_at).toLocaleDateString('tr-TR')} {new Date(note.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {note.updated_at && note.updated_at !== note.created_at && (
                            <p className="text-sm text-gray-500 dark:text-gray-500">
                              {t('projectDetail.updatedAt')}: {new Date(note.updated_at).toLocaleDateString('tr-TR')} {new Date(note.updated_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                          <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                            {note.created_by || 'Bilinmiyor'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEditNote(note)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title={t('projectDetail.editNote')}
                      >
                        <MdEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title={t('projectDetail.deleteNote')}
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add File Form Popup */}
      {showAddFileForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9998] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{t('projectDetail.addFile')}</h3>
              <button
                onClick={() => setShowAddFileForm(false)}
                className="text-gray-300 hover:text-white"
              >
                <MdClose size={24} />
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleAddFile}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('projectDetail.fileName')} *
                    </label>
                    <input
                      type="text"
                      value={newFile.project_file_name}
                      onChange={(e) => setNewFile({...newFile, project_file_name: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('projectDetail.fileDescription')}
                    </label>
                    <textarea
                      value={newFile.project_file_description}
                      onChange={(e) => setNewFile({...newFile, project_file_description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddFileForm(false)}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                  >
                    {t('projectDetail.addFile')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Form Popup */}
      {showAddNoteForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9998] flex items-center justify-center">
          <div className="relative mx-auto w-[800px] max-w-[90vw] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {editingNote ? t('projectDetail.editNoteTitle') : t('projectDetail.meetingNote')}
              </h3>
              <button
                onClick={() => {
                  setShowAddNoteForm(false);
                  setEditingNote(null);
                  setNewNote({ project_notes: '' });
                }}
                className="text-gray-300 hover:text-white"
              >
                <MdClose size={24} />
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={editingNote ? handleUpdateNote : handleAddNote}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Not *
                  </label>
                  <textarea
                    value={newNote.project_notes}
                    onChange={(e) => setNewNote({...newNote, project_notes: e.target.value})}
                    required
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('projectDetail.notePlaceholder')}
                  />
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddNoteForm(false);
                      setEditingNote(null);
                      setNewNote({ project_notes: '' });
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                  >
                    {editingNote ? t('projectDetail.updateNoteButton') : t('projectDetail.addNoteButton')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Member Popup */}
      {showAddMemberForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] max-w-[90vw] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {editingMember ? t('projectSettings.editMember') : t('projectDetail.addNewMember')}
              </h3>
              <button
                onClick={() => {
                  setShowAddMemberForm(false);
                  setEditingMember(null);
                  setNewMember({ project_member_name: '', assigned_hours: '', project_role: '', custom_member_name: '', custom_role: '' });
                }}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={20} />
              </button>
            </div>
            <div className="p-6 bg-white dark:bg-gray-800">
              <form onSubmit={editingMember ? handleUpdateMember : handleAddMember} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('projectDetail.memberName')}
                    </label>
                    <select
                      value={newMember.project_member_name}
                      onChange={(e) => setNewMember({ ...newMember, project_member_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">{t('projectSettings.selectAgent')}</option>
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.full_name}>
                          {agent.full_name}
                        </option>
                      ))}
                      <option value="Other">{t('projectSettings.other')}</option>
                    </select>
                    {newMember.project_member_name === 'Other' && (
                      <input
                        type="text"
                        value={newMember.custom_member_name}
                        onChange={(e) => setNewMember({ ...newMember, custom_member_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                        placeholder={t('projectSettings.enterCustomMemberName')}
                        required
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('projectDetail.projectRole')}
                    </label>
                    <select
                      value={newMember.project_role}
                      onChange={(e) => setNewMember({ ...newMember, project_role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">{t('projectSettings.selectRole')}</option>
                      <option value="Project Manager">{t('projectSettings.projectManager')}</option>
                      <option value="Consultant">{t('projectSettings.consultant')}</option>
                      <option value="Senior Consultant">{t('projectSettings.seniorConsultant')}</option>
                      <option value="Architect">{t('projectSettings.architect')}</option>
                      <option value="Other">{t('projectSettings.other')}</option>
                    </select>
                    {newMember.project_role === 'Other' && (
                      <input
                        type="text"
                        value={newMember.custom_role}
                        onChange={(e) => setNewMember({ ...newMember, custom_role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                        placeholder={t('projectSettings.enterCustomRole')}
                        required
                      />
                    )}
                  </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('projectDetail.assignedHours')}
                    </label>
                  <input
                    type="number"
                    value={newMember.assigned_hours}
                    onChange={(e) => setNewMember({ ...newMember, assigned_hours: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('projectDetail.enterHours')}
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMemberForm(false);
                      setEditingMember(null);
                      setNewMember({ project_member_name: '', assigned_hours: '', project_role: '', custom_member_name: '', custom_role: '' });
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
{t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                  >
{editingMember ? t('projectSettings.updateMember') : t('projectDetail.addMember')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Meeting Summary Popup */}
      {showMeetingSummary && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto w-[800px] max-h-[80vh] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">Toplantı Notları Özeti</h3>
              <button
                onClick={() => setShowMeetingSummary(false)}
                className="text-gray-300 hover:text-white"
              >
                <MdClose size={24} />
              </button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {meetingSummary}
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 flex justify-end">
              <button
                onClick={() => setShowMeetingSummary(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup Component */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{popup.title}</h3>
            </div>
            <div className="p-8 bg-white dark:bg-gray-800">
              <div className="text-center">
                <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                  {popup.message}
                </p>
                <div className="flex justify-center gap-4">
                  {popup.showCancel && (
                    <button
                      onClick={closePopup}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('common.cancel')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (popup.onConfirm) {
                        popup.onConfirm();
                      } else {
                        closePopup();
                      }
                    }}
                    className={`px-4 py-2 text-white rounded-md ${
                      popup.type === 'error' 
                        ? 'bg-red-600 hover:bg-red-700'
                        : popup.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-[#272c39] hover:bg-[#1e2230]'
                    }`}
                  >
                    {popup.type === 'confirm' ? t('common.confirm') : t('common.ok')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
